"use client"

import clsx from "clsx"
import * as React from "react"
import { useState } from "react"
import { TCounter } from "./TCounter"

/**
 * @description 도움말/에러/경고 메시지 또는 글자수 카운터. TField 하위에 배치
 * @example
 * ```tsx
 * <THelper message="필수 입력 항목입니다" error />
 * <THelper value={inputValue} maxChar={100} />
 * ```
 */
interface THelperProps {
	/** 현재 입력값. maxChar/maxByte 카운터 모드에서 사용 */
	value?: string
	/** 표시할 메시지. error=true 시 빨간색(#f33c3c), warning=true 시 노란색(#ffc300) */
	message?: string | React.ReactNode
	/** 최대 글자수 제한. 설정 시 카운터 모드 (value 필요) */
	maxChar?: number
	/** 최대 바이트수 제한. 설정 시 카운터 모드 (value 필요, 한글 3byte) */
	maxByte?: number
	/** 비활성화 여부 */
	disabled?: boolean
	/** 에러 상태. 메시지를 빨간색으로 표시 */
	error?: boolean
	/** 경고 상태. 메시지를 노란색(#ffc300)으로 표시 */
	warning?: boolean
	/** 추가 CSS 클래스 */
	className?: string
}

export const THelper = React.memo((props: THelperProps) => {
	const { value, message, maxChar, maxByte, disabled, error, warning, className } = props
	const [isLengthExceeded, setIsLengthExceeded] = useState<boolean>(false)

	const displayError = React.useMemo(() => isLengthExceeded || error, [isLengthExceeded, error])

	const handleLengthExceeded = React.useCallback((value: boolean) => {
		setIsLengthExceeded(value)
	}, [])

	if (!message && (maxChar !== undefined || maxByte !== undefined)) {
		return (
			<div className={clsx("THelper", className)}>
				<TCounter value={value} maxChar={maxChar} maxByte={maxByte} disabled={disabled} error={error} warning={warning} onLengthExceeded={handleLengthExceeded} />
			</div>
		)
	}
	if (!message) return null

	return (
		<div className={clsx("THelper", className)}>
			<p className={clsx({ disabled, error: displayError && !disabled, warning: warning && !disabled })}>{message}</p>
			<TCounter value={value} maxChar={maxChar} maxByte={maxByte} disabled={disabled} error={displayError} warning={warning} onLengthExceeded={handleLengthExceeded} />
		</div>
	)
})

THelper.displayName = "THelper"
